import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
	Box,
	IconButton,
	Stack,
	ThemeProvider,
	Typography,
} from '@mui/material';

import { useDTicketSubscription } from '@mopla/business-logic';
import { ESubscriptionStatus } from '@mopla/data-models';
import { passengerThemeV2 } from '@mopla/mui';
import {
	DTicketPageLayout,
	InformationIcon,
	MplActionCard,
	MplButton,
	MplDivider,
	MplRadioButton,
	MplRadioButtonWithDates,
} from '@mopla/ui';

import { usePassengerAppNavigate } from '../../../hooks';

import { DTicketEditPauseErrorModal } from './DTicketEditPauseErrorModal';
import { DTicketEditPauseInfoModal } from './DTicketEditPauseInfoModal';
import { DTicketEditPauseSuccessModal } from './DTicketEditPauseSuccessModal';
import { DTicketSubscriptionEditPauseSkeleton } from './DTicketSubscriptionEditPauseSkeleton';
import {
	useDTicketSubscriptionEditPause,
	useDTicketSubscriptionEditPauseForm,
	useDTicketSubscriptionEditPauseInfoModal,
} from './hooks';

export const DTicketSubscriptionEditPause = () => {
	const { t } = useTranslation('dticketSubscriptionEditPause');
	const { subscription } = useDTicketSubscription();

	const { navigateToUserDTicket, navigateToResumeSubscription } =
		usePassengerAppNavigate();

	const {
		control,
		selectedDuration,
		pauseDates,
		isDirty,
		onSubmit,
		canReschedule,
	} = useDTicketSubscriptionEditPauseForm();

	const {
		isSubscriptionLoading,
		isSuccessModalOpen,
		isErrorModalOpen,
		isPauseLoading,
		handleEditPause,
		closeSuccessModal,
		retryPause,
	} = useDTicketSubscriptionEditPause();

	const { isInfoModalOpen, openInfoModal, closeInfoModal } =
		useDTicketSubscriptionEditPauseInfoModal();

	const pauseOptions = [
		{ value: '1' as const, label: t('pause_options.one_month'), months: 1 },
		{ value: '2' as const, label: t('pause_options.two_months'), months: 2 },
		{ value: '3' as const, label: t('pause_options.three_months'), months: 3 },
	];

	const hasPause = subscription?.pauseStart && subscription?.pauseEnd;
	const isActivePause =
		subscription && subscription.status === ESubscriptionStatus.PAUSED;
	// The backend forbids revoking the cancellation (e.g. the subscription was
	// cancelled by an admin or failed payment) — editing the pause is unavailable
	const disallowCancelCancellation = !!subscription?.disallowCancelCancellation;

	useEffect(() => {
		if (!hasPause || disallowCancelCancellation) {
			navigateToUserDTicket();
		}
	}, [hasPause, disallowCancelCancellation, navigateToUserDTicket]);

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<DTicketPageLayout
				title={t('page_title')}
				onBack={navigateToUserDTicket}
				scrollable={true}
				dataTestId="dticket-subscription-editPause-page"
			>
				{isSubscriptionLoading ? (
					<DTicketSubscriptionEditPauseSkeleton />
				) : (
					<form onSubmit={onSubmit(handleEditPause)}>
						<Stack>
							<Stack gap={2}>
								{isActivePause ? (
									<MplActionCard
										title={t('edit_active_pause.resume_subscription.title')}
										description={
											<Trans
												i18nKey="dticketSubscriptionEditPause:edit_active_pause.resume_subscription.description"
												values={{
													pauseStart: pauseDates?.pauseStart,
													pauseEnd: pauseDates?.lastSavedPauseEnd,
												}}
												components={{
													bold: <strong />,
												}}
											/>
										}
										ctaText={t('edit_active_pause.resume_subscription.button')}
										buttonTestId="dticket-subscriptionEditPause-resumeSubscription-button"
										onCtaClick={navigateToResumeSubscription}
									/>
								) : (
									<MplActionCard
										title={t(
											'edit_upcoming_pause.continue_subscription_without_pause.title'
										)}
										description={t(
											'edit_upcoming_pause.continue_subscription_without_pause.description'
										)}
										ctaText={t(
											'edit_upcoming_pause.continue_subscription_without_pause.button'
										)}
										buttonTestId="dticket-subscriptionEditPause-resumeSubscription-button"
										onCtaClick={navigateToResumeSubscription}
									/>
								)}

								{!isActivePause && canReschedule && (
									<>
										<Box sx={{ py: 1 }}>
											<MplDivider message={t('divider_text')} />
										</Box>

										<Stack gap="4px">
											<Stack
												direction="row"
												alignItems="center"
												justifyContent="space-between"
											>
												<Typography variant="h4-sb" color="primary.main">
													{t('section_title')}
												</Typography>
												<IconButton
													sx={{ padding: '2px' }}
													onClick={openInfoModal}
													data-testid="dticket-subscriptionEditPause-info-button"
												>
													<InformationIcon />
												</IconButton>
											</Stack>
											<Typography variant="body2" color="primary.main">
												{t('edit_upcoming_pause.section_description')}
											</Typography>
										</Stack>

										<Controller
											name="duration"
											control={control}
											rules={{ required: true }}
											render={({ field }) => (
												<Stack gap="12px">
													{pauseOptions.map((option) => {
														const isSelected = field.value === option.value;

														if (isSelected && pauseDates) {
															return (
																<MplRadioButtonWithDates
																	key={option.value}
																	text={option.label}
																	checked={true}
																	onChange={() => field.onChange(option.value)}
																	ticketValidUntilLabel={t(
																		'ticket_valid_until'
																	)}
																	ticketValidUntilDate={
																		pauseDates.lastDateBeforePause
																	}
																	ticketActiveFromLabel={t(
																		'ticket_active_from'
																	)}
																	ticketActiveFromDate={pauseDates.activeFrom}
																	data-testid={`dticket-subscriptionEditPause-pauseOption-${option.value}-radio`}
																/>
															);
														}

														return (
															<MplRadioButton
																key={option.value}
																text={option.label}
																checked={isSelected}
																onChange={() => field.onChange(option.value)}
																data-testid={`dticket-subscriptionEditPause-pauseOption-${option.value}-radio`}
															/>
														);
													})}
												</Stack>
											)}
										/>
										<MplButton
											type="submit"
											variant="contained"
											color="primary"
											fullWidth
											disabled={!selectedDuration || !isDirty || isPauseLoading}
											showLoader={isPauseLoading}
											testId="dticket-subscriptionEditPause-editPauseButton-button"
										>
											{t('edit_pause_button')}
										</MplButton>
									</>
								)}
							</Stack>
						</Stack>
					</form>
				)}
			</DTicketPageLayout>
			<DTicketEditPauseInfoModal
				open={isInfoModalOpen}
				onClose={closeInfoModal}
			/>

			{pauseDates && (
				<DTicketEditPauseSuccessModal
					open={isSuccessModalOpen}
					onClose={closeSuccessModal}
					activeFromDate={pauseDates.activeFrom}
				/>
			)}

			<DTicketEditPauseErrorModal
				open={isErrorModalOpen}
				onRetry={retryPause}
			/>
		</ThemeProvider>
	);
};
